import type { Layer } from '../../../types/layer';
import type { LayerRegistry, BaseLayer } from '../../layers2';
import { logger } from '../../../utils/logger';

const log = logger('initializeLayers');

const DEFAULT_TIMEOUT_MS = 8000;

interface InitializeOptions {
  mapLoaded: boolean;
  timeoutMs?: number;
}

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms
    );
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/**
 * Initialize a batch of layers concurrently.
 *
 * Each layer's create/initialize/enable runs independently — a hang or failure
 * in one layer cannot block siblings. Per-layer work is bounded by `timeoutMs`
 * (default 8s) so an unresponsive WMS source can't white-screen the map.
 *
 * On failure or timeout the layer is removed from the registry so the user
 * doesn't see a half-initialized layer that can't be toggled.
 */
export async function initializeLayersInParallel(
  layers: Layer[],
  registry: LayerRegistry,
  opts: InitializeOptions
): Promise<void> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  await Promise.allSettled(
    layers.map(async (layer) => {
      if (registry.has(layer.id)) return;

      let instance: BaseLayer | null = null;
      try {
        instance = registry.createLayer(layer);
        registry.add(instance);

        await withTimeout(
          instance.initialize(),
          timeoutMs,
          `${layer.name} initialize`
        );

        if (layer.enabled && opts.mapLoaded) {
          try {
            await withTimeout(
              instance.enable(),
              timeoutMs,
              `${layer.name} enable`
            );
            log.success(`${layer.name} enabled`);
          } catch (enableErr) {
            log.error(`${layer.name} enable failed:`, enableErr);
          }
        }
      } catch (err) {
        log.error(`${layer.name} create/init failed:`, err);
        // If we managed to add the instance before the failure, remove it so
        // the registry doesn't keep a broken half-initialized layer.
        if (instance) {
          try {
            await registry.remove(layer.id);
          } catch {
            /* best-effort cleanup */
          }
        }
      }
    })
  );
}
